import { Clock3, Flame, Hourglass } from "lucide-react";

/**
 * Live "class is happening" badge shown on cards & map popups.
 *
 * Reads ``class_status`` (shape produced by /backend/schedule_parser.py):
 *   { state, label, minutes_until_open, minutes_until_close }
 *
 * Rendered states:
 *   - starts_soon  → orange filled, flame icon (highest signal)
 *   - closing_soon → black filled, hourglass icon
 *   - open_now     → outlined, clock icon (low-priority signal)
 *   - closed / unknown → nothing
 *
 * `size` controls compact (map popup) vs default (card).
 */
export function StatusBadge({ status, size = "default", className = "" }) {
    if (!status || !status.state || !status.label) return null;
    const { state, label } = status;
    if (state === "closed" || state === "unknown") return null;

    const base =
        size === "compact"
            ? "px-1.5 py-0.5 text-[9px] gap-1"
            : "px-2 py-1 text-[10px] gap-1.5";
    const iconSize = size === "compact" ? "h-2.5 w-2.5" : "h-3 w-3";

    const variants = {
        starts_soon: "bg-primary text-background border border-primary",
        closing_soon: "bg-foreground text-background border border-foreground",
        open_now: "bg-background text-foreground border border-foreground",
    };
    const icons = {
        starts_soon: <Flame className={iconSize} />,
        closing_soon: <Hourglass className={iconSize} />,
        open_now: <Clock3 className={iconSize} />,
    };

    return (
        <span
            className={`inline-flex items-center font-mono-label uppercase tracking-widest ${base} ${variants[state]} ${className}`}
            data-testid={`status-badge-${state}`}
            title={label}
        >
            {icons[state]}
            {label}
        </span>
    );
}
